import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { shipmentFormStopsToDeviceApiStops } from '@/components/shipment/form/utils/shipment-form-payload';

const CENTER_EPS = 1e-7;

type StopPayload = Record<string, unknown>;

type LatLng = { lat: number; lng: number };

function normalizeCenter(center: unknown): LatLng | null {
  if (center == null || typeof center !== 'object') return null;
  const c = center as Record<string, unknown>;
  const lat = c.lat;
  const lng = c.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function centerLatLngChanged(before: unknown, after: unknown): boolean {
  const a = normalizeCenter(before);
  const b = normalizeCenter(after);
  if (a === null && b === null) return false;
  if (a === null || b === null) return true;
  return (
    Math.abs(a.lat - b.lat) > CENTER_EPS || Math.abs(a.lng - b.lng) > CENTER_EPS
  );
}

function stopId(payload: StopPayload): number | null {
  const id = payload.id;
  if (typeof id === 'number' && Number.isFinite(id) && id > 0) return id;
  if (typeof id === 'string' && id.trim() !== '') {
    const parsed = Number(id);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

/**
 * Mirrors {@link SyncShipmentStopsAndRoutes} route invalidation rules on device-api.
 */
export function stopPayloadsInvalidateServerRoutes(
  persisted: readonly StopPayload[] | null,
  current: readonly StopPayload[],
): boolean {
  if (persisted == null) {
    return current.some((stop) => stopId(stop) == null);
  }

  const persistedById = new Map<number, StopPayload>();
  for (const stop of persisted) {
    const id = stopId(stop);
    if (id != null) persistedById.set(id, stop);
  }

  const currentIds = new Set<number>();
  for (const stop of current) {
    const id = stopId(stop);
    if (id == null) return true;
    currentIds.add(id);
  }

  for (const id of persistedById.keys()) {
    if (!currentIds.has(id)) return true;
  }

  for (const cur of current) {
    const id = stopId(cur);
    if (id == null) continue;
    const prev = persistedById.get(id);
    if (!prev) continue;

    if (Number(prev.sequence) !== Number(cur.sequence)) return true;

    const typeBefore = prev.type;
    const typeAfter = cur.type;
    if (typeBefore !== typeAfter) return true;

    if (typeAfter === 'geofence') {
      if (Number(prev.geofence_id ?? 0) !== Number(cur.geofence_id ?? 0)) {
        return true;
      }
    } else if (centerLatLngChanged(prev.center, cur.center)) {
      return true;
    }
  }

  return false;
}

export function stopPayloadInvalidatesServerRoutesFromPersistedSnapshot(
  persistedSnapshot: string | null,
  currentRows: ShipmentFormStopRow[],
): boolean {
  const current = shipmentFormStopsToDeviceApiStops(currentRows);
  if (persistedSnapshot == null) {
    return stopPayloadsInvalidateServerRoutes(null, current);
  }
  const persisted = JSON.parse(persistedSnapshot) as StopPayload[];
  return stopPayloadsInvalidateServerRoutes(persisted, current);
}
